import router from '@/router'
import { SNK, numFixed } from '@/helpers/helpers'
import { numFixedUnit } from '@gto/helpers/number-formatting'
import * as R from 'remeda'
import {
  getHistoryUrlParams,
  setActiveHistorySpot,
  getEventsTreePreviewApi,
} from '@/store/gamestate/gamestate'
import { findLast, omit } from 'lodash'
import { getRouteNameByStudyTab } from '@/store/router/router'
import { rangesDataSkeleton } from '@/helpers/solution'
import {
  getSolfigrFields,
  getFlopOnTurnAndRiverDiffs,
  getFormError,
  getSolfigrFormApiPostData,
  getSizesSkeleton,
  getSolfigrSizesFromApiData,
  getSolfigrPlayer,
  getBlinds,
  getTournamentStructure,
  getIcmPlayersAndStacks,
  getIcmHandDetails,
  validateTournamentStructure,
  validateHandDetails,
  getPlayersCloningFirstPlayer,
} from '@/store/solfigr/solfigr_helpers'
import defaults from '@/store/solfigr/mocks/defaults.json'
import { namespaces } from '@/helpers/constants'

const getInitialState = ({ namespace, slfrm_startStreet }) => {
  const conf = getSolfigrFields()

  const res = {
    slfrm_namespace: namespace,
    slfrm_shortcut: {
      player_one: '',
      player_two: '',
      action: '',
      gameformat: null,
      label_id: null,
      apiShortcut: null,
      noActions: false,
    },
    slfrm_startStreet: slfrm_startStreet || 'flop',

    slfrm_tournamentType: 'chipev', // chipev, icm

    slfrm_icm_tournament: getTournamentStructure({
      type: conf.tournament_types.default,
      total_entrants: conf.total_entrants.default,
      payout_levels: conf.payout_levels.default,
      bounty_instant_percentage: conf.bounty_instant_percentage.default,
    }),
    slfrm_icm_handDetails: {
      pot_size: conf.pot_size.default,
      stack_IP: conf.stack_IP.default,
      stack_OOP: conf.stack_OOP.default,
      stack_OOP1: conf.stack_OOP.default,
      bounty_instant_IP: conf.bounty_instant.default,
      bounty_instant_OOP: conf.bounty_instant.default,
      bounty_total_IP: conf.bounty_total.default,
      bounty_total_OOP: conf.bounty_total.default,
      errors: {
        total_errors: 0,
        error_stack_OOP: false,
        error_stack_IP: false,
        error_stack_OOP1: false,
      },
    },
    slfrm_icm_playersAndStacks: {
      stacks_distribution_type: 'sdt_manual', // sdt_manual, sdt_lognormal
      lognormal_deviation: conf.lognormal_deviation.default,
      num_of_players_remaining: conf.num_of_players_remaining.default,
      avg_stack: conf.avg_stack.getDefault({
        stacks: [conf.stack_IP.default, conf.stack_OOP.default],
      }),
      avg_bounty: conf.avg_bounty.getDefault(),
      stacks_manual: [0.001, 0.001, 0.001],
      stacks_defined: [],
    },

    slfrm_stack_preflop: conf.stack.default,
    slfrm_stack_flop: conf.stack.default,

    // 3way stacks
    slfrm_stack_flop_IP: conf.stack.default,
    slfrm_stack_flop_OOP: conf.stack.default,
    slfrm_stack_flop_OOP1: conf.stack.default,

    slfrm_pot: conf.pot.default,
    slfrm_ante: conf.ante.default,
    slfrm_blind: conf.blind.default,

    slfrm_ante_distribution_method: 'PER_PLAYER',
    slfrm_allow_limp: false,

    // maybe split to preflop flop
    slfrm_rakeCap_preflop: 0,
    slfrm_rakePercent_preflop: 0,
    slfrm_rakeCap_flop: 0,
    slfrm_rakePercent_flop: 0,
    slfrm_preflopRakeType: conf.preflopRakeType.default,

    slfrm_allowRake_preflop: false,
    slfrm_allowRake_flop: false,

    // Allowed positions: "IP", "OOP", "OOP+1", "OOP+2", "OOP+3", "OOP+4", "OOP+5", "OOP+6", "OOP+7"
    slfrm_flop_playerIP: {
      position: 'IP',
      display_position: 'IP',
      combos: 1,
      percentage: 1,
      groupRange: [],
    },
    slfrm_flop_playerOOP: {
      position: 'OOP',
      display_position: 'OOP',
      combos: 1,
      percentage: 1,
      groupRange: [],
    },
    slfrm_flop_playerOOP1: undefined,
    slfrm_preflop_playerIP: {
      position: 'IP',
      display_position: 'SB',
    },
    slfrm_preflop_playerOOP: {
      position: 'OOP',
      display_position: 'BB',
    },

    slfrm_sizes: getSizesSkeleton(),
    slfrm_useFlopSizesOnAllStreets: true,

    slfrm_playersCloningFirstPlayer: { player_ip: true, player_oop1: true },

    slfrm_allin_threshold: conf.allin_threshold.default,
    slfrm_allin_if_less_than: conf.allin_if_less_than.default,
    slfrm_merge_sizes_threshold_preflop: conf.merge_sizes_threshold.default_preflop,
    slfrm_merge_sizes_threshold_flop: conf.merge_sizes_threshold.default_flop,
    slfrm_max_num_raises: conf.max_num_raises.default,

    slfrm_treeOperations: [],

    // -- crud purposes
    slfrm_selectedTree: {
      id: '',
      name: '',
    },
    slfrm_selectedRange: {
      id: '',
      name: '',
    },
  }
  return res
}

const getters = {
  slfrm_tournamentType: state => state.slfrm_tournamentType,

  slfrm_icm_tournament: state => state.slfrm_icm_tournament,
  slfrm_icm_playersAndStacks: state => state.slfrm_icm_playersAndStacks,
  slfrm_icm_handDetails: state => state.slfrm_icm_handDetails,

  slfrm_selectedTree: state => state.slfrm_selectedTree,
  slfrm_selectedRange: state => state.slfrm_selectedRange,
  slfrm_treeOperations: state => state.slfrm_treeOperations,
  slfrm_rakePercent_preflop: state => state.slfrm_rakePercent_preflop,
  slfrm_rakeCap_preflop: state => state.slfrm_rakeCap_preflop,
  slfrm_allowRake_preflop: state => state.slfrm_allowRake_preflop,
  slfrm_allowRake_flop: state => state.slfrm_allowRake_flop,
  slfrm_rakePercent_flop: state => state.slfrm_rakePercent_flop,
  slfrm_rakeCap_flop: state => state.slfrm_rakeCap_flop,
  slfrm_preflopRakeType: state => state.slfrm_preflopRakeType,
  slfrm_shortcut: state => state.slfrm_shortcut,
  slfrm_startStreet: state => state.slfrm_startStreet,
  slfrm_stack_preflop: state => state.slfrm_stack_preflop,
  slfrm_stack_flop: state => state.slfrm_stack_flop,
  slfrm_stack_flop_IP: state => state.slfrm_stack_flop_IP,
  slfrm_stack_flop_OOP: state => state.slfrm_stack_flop_OOP,
  slfrm_stack_flop_OOP1: state => state.slfrm_stack_flop_OOP1,
  slfrm_pot: state => state.slfrm_pot,
  slfrm_ante: state => state.slfrm_ante,
  slfrm_ante_distribution_method: state => state.slfrm_ante_distribution_method,
  slfrm_blind: state => state.slfrm_blind,
  slfrm_allow_limp: state => state.slfrm_allow_limp,
  slfrm_preflop_players: state => [state.slfrm_preflop_playerOOP, state.slfrm_preflop_playerIP],
  slfrm_flop_players: state =>
    [state.slfrm_flop_playerOOP, state.slfrm_flop_playerOOP1, state.slfrm_flop_playerIP].filter(
      p => p !== undefined && (state.slfrm_tournamentType === 'chipev' || p.position !== 'OOP+1'),
    ),
  slfrm_players: (state, getters) =>
    state.slfrm_startStreet === 'flop' ? getters.slfrm_flop_players : getters.slfrm_preflop_players,
  slfrm_sizes: state => state.slfrm_sizes,
  slfrm_useFlopSizesOnAllStreets: state => state.slfrm_useFlopSizesOnAllStreets,
  slfrm_playersCloningFirstPlayer: state => state.slfrm_playersCloningFirstPlayer,
  slfrm_allin_threshold: state => state.slfrm_allin_threshold,
  slfrm_allin_if_less_than: state => state.slfrm_allin_if_less_than,
  slfrm_merge_sizes_threshold_preflop: state => state.slfrm_merge_sizes_threshold_preflop,
  slfrm_merge_sizes_threshold_flop: state => state.slfrm_merge_sizes_threshold_flop,
  slfrm_max_num_raises: state => state.slfrm_max_num_raises,
  slfrm_formApiPostData: (state, _getters, _rootState, rootGetters) => {
    const isPreflop = state.slfrm_startStreet === 'preflop'
    const res = getSolfigrFormApiPostData({
      allin_threshold: state.slfrm_allin_threshold,
      allin_if_less_than: state.slfrm_allin_if_less_than,
      merge_sizes_threshold: isPreflop
        ? state.slfrm_merge_sizes_threshold_preflop
        : state.slfrm_merge_sizes_threshold_flop,
      max_num_raises: state.slfrm_max_num_raises,

      playerIP: isPreflop ? state.slfrm_preflop_playerIP : state.slfrm_flop_playerIP,
      playerOOP: isPreflop ? state.slfrm_preflop_playerOOP : state.slfrm_flop_playerOOP,
      playerOOP1:
        isPreflop || !state.slfrm_flop_playerOOP1 || state.slfrm_tournamentType === 'icm'
          ? undefined
          : state.slfrm_flop_playerOOP1,
      stack: isPreflop ? state.slfrm_stack_preflop : state.slfrm_stack_flop,

      // 3way stacks
      stack_IP: isPreflop ? 0 : state.slfrm_stack_flop_IP,
      stack_OOP: isPreflop ? 0 : state.slfrm_stack_flop_OOP,
      stack_OOP1: isPreflop ? 0 : state.slfrm_stack_flop_OOP1,

      allowRake: isPreflop ? state.slfrm_allowRake_preflop : state.slfrm_allowRake_flop,
      rakeCap: isPreflop ? state.slfrm_rakeCap_preflop : state.slfrm_rakeCap_flop,
      rakePercent: isPreflop ? state.slfrm_rakePercent_preflop : state.slfrm_rakePercent_flop,
      preflopRakeType: isPreflop ? state.slfrm_preflopRakeType : null,

      treeOperations: state.slfrm_treeOperations,
      startStreet: state.slfrm_startStreet,
      sizes: state.slfrm_sizes,
      pot: state.slfrm_pot,
      ante: state.slfrm_ante,
      ante_distribution_method: state.slfrm_ante_distribution_method,
      blind: state.slfrm_blind,
      allow_limp: state.slfrm_allow_limp,

      tournamentType: state.slfrm_tournamentType,
      icmTournament: state.slfrm_icm_tournament,
      icmPlayersAndStacks: state.slfrm_icm_playersAndStacks,
      icmHandDetails: state.slfrm_icm_handDetails,
    })
    return res
  },
  slfrm_getFormError:
    (state, getters) =>
    ({ formResult = null } = {}) => {
      const res = getFormError({ formResult: formResult || getters.slfrm_formApiPostData })
      return res
    },
  slfrm_shouldShowAddPlayerButton: (state, getters) =>
    state.slfrm_startStreet === 'flop' && getters.slfrm_flop_players.length < 3,
  slfrm_showComingSoonPlayerIcm: state =>
    state.slfrm_startStreet === 'flop' && state.slfrm_tournamentType === 'icm',
}

const actions = {
  slfrm_addPlayer({ state, commit, dispatch, getters }) {
    commit('slfrm_set', {
      key: 'slfrm_flop_playerOOP1',
      value: {
        ...state.slfrm_flop_playerOOP,
        position: 'OOP+1',
        display_position: 'OOP+1',
      },
    })

    // Always force fixed bet sizes when adding a player (third one)
    dispatch('slfrm_setFixedBetSizes')

    dispatch('slfrm_getTreePreview', {
      fromStart: false,
    })
  },
  slfrm_setFixedBetSizes({ state, commit }) {
    // force all bet sizes to fixed with three players
    // we should eventually not need this once automatic and dynamic are implemented for 3way in the engine
    const updatedSizes = R.clone(state.slfrm_sizes)
    const streets = ['preflop', 'flop', 'turn', 'river']
    streets.forEach(street => {
      if (updatedSizes[street]) {
        Object.keys(updatedSizes[street]).forEach(player => {
          if (updatedSizes[street][player] && updatedSizes[street][player].mode !== undefined) {
            updatedSizes[street][player].mode = 'fixed'
          }
        })
      }
    })
    commit('slfrm_set', {
      key: 'slfrm_sizes',
      value: updatedSizes,
    })
  },
  slfrm_removePlayer({ state, commit, dispatch }, player) {
    // If removing OOP or IP player copy OOP+1 player to it
    if (player.position === 'OOP' || player.position === 'IP') {
      commit('slfrm_set', {
        key: player.position === 'OOP' ? 'slfrm_flop_playerOOP' : 'slfrm_flop_playerIP',
        value: {
          ...state.slfrm_flop_playerOOP1,
          position: player.position,
          display_position:
            // Keep display_position of OOP+1 player if it's not relative
            state.slfrm_flop_playerOOP1.display_position === 'OOP+1'
              ? player.position
              : state.slfrm_flop_playerOOP1.display_position,
        },
      })
      // Copy betsizes
      const updatedSizes = R.clone(state.slfrm_sizes)
      const streets = ['preflop', 'flop', 'turn', 'river']
      streets.forEach(street => {
        if (updatedSizes[street]) {
          const player_key = player.position === 'OOP' ? 'player_oop' : 'player_ip'
          updatedSizes[street][player_key] = { ...updatedSizes[street]?.player_oop1 }
        }
      })
      commit('slfrm_set', {
        key: 'slfrm_sizes',
        value: updatedSizes,
      })
      // Copy cloning of OOP state from OOP+1 to IP if IP is removed
      if (player.position === 'IP') {
        commit('slfrm_set', {
          key: 'slfrm_playersCloningFirstPlayer',
          value: {
            ...state.slfrm_playersCloningFirstPlayer,
            player_ip: state.slfrm_playersCloningFirstPlayer.player_oop1,
          },
        })
      }
    }
    // Remove OOP+1 player
    commit('slfrm_set', {
      key: 'slfrm_flop_playerOOP1',
      value: undefined,
    })
    dispatch('slfrm_getTreePreview', {
      fromStart: false,
    })
  },
  slfrm_applyApiShortcuts({ state, commit, dispatch, getters }, result) {
    if (result?.response?.status !== 200 || !result.response?.data) {
      return
    }

    // reset tree-preview
    commit(`gamestate_set`, {
      key: 'history_spots',
      value: [],
    })

    const apiData = result.response?.data
    const { gameformat } = getters

    const selectedPositions = [
      state.slfrm_shortcut.player_one,
      state.slfrm_shortcut.player_two,
    ].filter(i => i)
    const { action } = state.slfrm_shortcut

    let apiShortcut = null
    if (selectedPositions.length === 2 && action) {
      apiShortcut = apiData?.find(
        i =>
          i.label.street === 'FLOP' &&
          i.label.positions.every(p => selectedPositions.includes(p)) &&
          i.label.type === action,
      )
    } else {
      apiShortcut = apiData?.find(i => i.label.street === 'FLOP')
      if (apiShortcut) {
        const l_shortcut = {
          ...state.slfrm_shortcut,
          player_one: apiShortcut?.label.positions[0],
          player_two: apiShortcut?.label.positions[1],
        }
        commit(`slfrm_set`, { key: 'slfrm_shortcut', value: l_shortcut })
      }
    }

    if (!apiShortcut) {
      console.log('slfrm_applyApiShortcuts: noActions')
    }

    const shrtLabel = apiShortcut?.label
    const res = {
      player_one: shrtLabel?.positions?.[0],
      player_two: shrtLabel?.positions?.[1],
      action: shrtLabel?.type,
      gameformat,
      label_id: shrtLabel?.id,
      apiShortcut,
      noActions: !apiShortcut,
    }
    commit('slfrm_set', { key: 'slfrm_shortcut', value: res })

    if (apiShortcut) {
      dispatch('api_poker_player_ranges', { namespace: state.slfrm_namespace }, { root: true })
    } else {
      dispatch('smfrm_setFallbackRanges')
    }
  },
  smfrm_setFallbackRanges({ state, commit, dispatch, getters }, result) {
    const mockResult = {
      response: {
        status: 200,
        data: {
          starting_street: 'FLOP',
          players: [
            { position: 'IP', table_position: 'IP', range: defaults.range_ip, stack: 97.5 },
            { position: 'OOP', table_position: 'OOP', range: defaults.range_oop, stack: 97.5 },
          ],
          pot: 5.5, // recommended default
        },
      },
    }
    dispatch('slfrm_applyApiPreset', { ...mockResult })
  },
  slfrm_applyApiCustomTree({ state, commit, dispatch, getters }, result) {
    if (
      result?.response?.status !== 200 ||
      (!result.response?.data && !getters.sol_elite.is_preflop)
    ) {
      const status = result?.error?.response?.status
      dispatch(
        'messages',
        {
          type: 'error',
          status: 'err_message',
          statusMessages: {
            _err_message: {
              i18n: `messages.spot_ranges_prefill_failed_status_xxx`,
              i18nParams: { status: status || 'unknown' },
            },
          },
        },
        { root: true },
      )
      dispatch(
        'api_poker_shortcuts',
        {
          namespace: state.slfrm_namespace,
          action: `${state.slfrm_namespace}/slfrm_applyApiShortcuts`,
          actionArgs: {
            solfigrPresetMode: 'createnew',
          },
        },
        { root: true },
      )
      return
    }
    dispatch('slfrm_applyApiPreset', result)
  },
  slfrm_applyApiPreset({ state, commit, dispatch, rootGetters, getters }, result) {
    const apiData = result?.response?.data
    if (result?.response?.status !== 200 || !apiData) {
      const status = result?.error?.response?.status
      dispatch(
        'messages',
        {
          type: 'error',
          status: 'err_message',
          statusMessages: {
            _err_message: {
              i18n:
                status === 400
                  ? `messages.spot_ranges_prefill_failed`
                  : `messages.spot_ranges_prefill_failed_status_xxx`,
              i18nParams: { status: status || 'unknown' },
            },
          },
        },
        { root: true },
      )
      dispatch('smfrm_setFallbackRanges')
      return
    }

    // reset tree-preview
    commit('gamestate_set', {
      key: 'history_spots',
      value: [],
    })

    const solfigrPresetMode = result.solfigrPresetMode || 'createnew'

    const api_ip_player = apiData.players?.find(i => i.position === 'IP')
    const api_oop_player = apiData.players?.find(i => i.position === 'OOP')
    const api_oop1_player = apiData.players?.find(i => i.position === 'OOP+1')
    const effectiveStack = Math.min(api_ip_player?.stack, api_oop_player?.stack)
    const stack = numFixedUnit(effectiveStack, 'bb')

    state.slfrm_treeOperations =
      solfigrPresetMode === 'createnew' ? [] : apiData?.tree_operations || []

    const isPreflop = apiData.starting_street === 'PREFLOP'

    if (!result?.actionArgs?.range_prefill) {
      state.slfrm_startStreet = apiData.starting_street.toLowerCase()
    }

    if (solfigrPresetMode !== 'restorebetsizesonly') {
      if (!isPreflop || result?.actionArgs?.range_prefill) {
        const { tableGroups } = rangesDataSkeleton()
        const playerIP = getSolfigrPlayer({ tableGroups, api_player: api_ip_player })
        const playerOOP = getSolfigrPlayer({ tableGroups, api_player: api_oop_player })
        const playerOOP1 = api_oop1_player
          ? getSolfigrPlayer({ tableGroups, api_player: api_oop1_player })
          : undefined

        const pot = numFixedUnit(Number(apiData.pot), 'bb')

        state.slfrm_rakePercent_flop = numFixedUnit(apiData.rake?.pct_of_pot || 0, 'rake_percent')
        state.slfrm_rakeCap_flop = numFixedUnit(apiData.rake?.cap_in_chips || 0, 'rake_cap')
        state.slfrm_allowRake_flop = !!(state.slfrm_rakePercent_flop || state.slfrm_rakeCap_flop)

        state.slfrm_preflopRakeType = // added lately - some early trees might not have this value
          apiData.rake?.preflop_rake_type || getSolfigrFields().preflopRakeType.default

        state.slfrm_stack_flop = stack

        if (rootGetters.user_has_flag('SOL_3WAY')) {
          state.slfrm_stack_flop_IP = numFixedUnit(api_ip_player.stack, 'bb')
          state.slfrm_stack_flop_OOP = numFixedUnit(api_oop_player.stack, 'bb')
          if (api_oop1_player) {
            state.slfrm_stack_flop_OOP1 = numFixedUnit(api_oop1_player.stack, 'bb')
          }
        }

        state.slfrm_pot = pot
        state.slfrm_flop_playerIP = playerIP
        state.slfrm_flop_playerOOP = playerOOP
        state.slfrm_flop_playerOOP1 = playerOOP1

        const tournamentData = apiData.tournament_data
        state.slfrm_tournamentType = tournamentData ? 'icm' : 'chipev'
        if (state.slfrm_tournamentType === 'icm') {
          const conf = getSolfigrFields()
          const icmStructure = tournamentData.tournament_structure
          const bounty_instant_percentage =
            icmStructure.bounty_instant_percentage || conf.bounty_instant_percentage.default

          state.slfrm_icm_tournament = getTournamentStructure({
            type: icmStructure.type,
            total_entrants: icmStructure.total_entrants,
            payout_levels: icmStructure.payout_levels,
            bounty_instant_percentage,
          })
          const icmHandDetails = getIcmHandDetails({
            pot,
            api_oop_player,
            api_ip_player,
            api_oop1_player,
            type: icmStructure.type,
            bounty_instant_percentage,
          })
          state.slfrm_icm_handDetails = icmHandDetails
          state.slfrm_icm_playersAndStacks = getIcmPlayersAndStacks({
            tournamentData,
            icmHandDetails,
          })
          dispatch('slrfm_updateIcmInheritedValues', {
            getTreePreview: false,
          })
        }
      } else if (isPreflop) {
        state.slfrm_rakePercent_preflop = numFixedUnit(
          apiData.rake?.pct_of_pot || 0,
          'rake_percent',
        )
        state.slfrm_rakeCap_preflop = numFixedUnit(apiData.rake?.cap_in_chips || 0, 'rake_cap')
        state.slfrm_allowRake_preflop = !!(
          state.slfrm_rakePercent_preflop || state.slfrm_rakeCap_preflop
        )
        state.slfrm_stack_preflop = stack
        state.slfrm_ante_distribution_method = apiData.ante_distribution_method
        switch (state.slfrm_ante_distribution_method) {
          case 'TOTAL':
            state.slfrm_ante = [api_ip_player, api_oop_player].reduce(
              (acc, p) => acc + numFixedUnit(Number(p.ante), 'bb'),
              0,
            )
            break
          case 'BB_ANTE':
            state.slfrm_ante = numFixedUnit(Number(api_oop_player.ante), 'bb')
            break
          default:
            state.slfrm_ante = numFixedUnit(Number(api_ip_player.ante), 'bb')
            break
        }
        state.slfrm_allow_limp =
          apiData.bet_sizes.street_bet_sizes[0].position_bet_sizes[0].allow_limp
        state.slfrm_blind = Number(getBlinds(apiData.players)?.SB)

        state.slfrm_preflop_playerIP = {
          position: api_ip_player.position,
          display_position: api_ip_player.position === 'OOP' ? 'BB' : 'SB',
        }
        state.slfrm_preflop_playerOOP = {
          position: api_oop_player.position,
          display_position: api_oop_player.position === 'OOP' ? 'BB' : 'SB',
        }
      }
    }

    if (apiData.bet_sizes) {
      const mergeTreshold = apiData.bet_sizes.merge_sizes_threshold
      state.slfrm_allin_threshold = apiData.bet_sizes.allin_threshold
      state.slfrm_allin_if_less_than = apiData.bet_sizes.allin_if_less_than
      state.slfrm_merge_sizes_threshold_preflop = isPreflop
        ? mergeTreshold
        : state.slfrm_merge_sizes_threshold_preflop
      state.slfrm_merge_sizes_threshold_flop = !isPreflop
        ? mergeTreshold
        : state.slfrm_merge_sizes_threshold_flop
      state.slfrm_sizes = getSolfigrSizesFromApiData({
        street_bet_sizes: apiData.bet_sizes.street_bet_sizes,
      })
      state.slfrm_max_num_raises = // added lately - some early trees might not have this value
        apiData.bet_sizes.max_num_raises === undefined
          ? getSolfigrFields().max_num_raises.default
          : apiData.bet_sizes.max_num_raises

      state.slfrm_useFlopSizesOnAllStreets =
        getFlopOnTurnAndRiverDiffs({
          sizes: state.slfrm_sizes,
          multiway: rootGetters.user_has_flag('SOL_3WAY'),
        }).diffs_total === 0

      state.slfrm_playersCloningFirstPlayer = getPlayersCloningFirstPlayer({
        sizes: state.slfrm_sizes,
        players: ['player_ip', 'player_oop1'],
      })
    }

    if (result?.editSolution) {
      commit(`slfrm_set`, { key: 'slfrm_selectedTree', value: apiData })
    }

    // Force fixed bet sizes for 3+ players after prefill
    if (getters.slfrm_players.length > 2) {
      dispatch('slfrm_setFixedBetSizes')
    }

    dispatch('slfrm_getTreePreview', {
      fromStart: true,
    })
  },
  async slfrm_createNewTree({ state, dispatch, getters, rootGetters }, { solfigrNewTreeMode }) {
    let storeAction = ''
    let namespace = ''

    const pathName = getRouteNameByStudyTab(router, rootGetters)
    if (
      pathName === 'StudySolutions' ||
      pathName === 'StudyReports' ||
      pathName === 'PracticeRangeBuilder'
    ) {
      storeAction = `slfrm_handleNewTree`

      // TODO: #REFACTOR:NAMESPACES
      const solRepNamespace = {
        StudySolutions: namespaces.STUDY,
        StudyReports: namespaces.REPORTS,
      }[pathName]

      storeAction = SNK('slfrm_handleNewTree', solRepNamespace)
      namespace = solRepNamespace
    } else if (pathName === 'PracticeTrainer' || pathName === 'PracticeDrills') {
      storeAction = SNK('slfrm_handleNewTree', state.slfrm_namespace)
      namespace = state.slfrm_namespace
    }
    await dispatch(
      'api_poker_custom_solution_tree_create',
      {
        data: getters.slfrm_formApiPostData,
        action: storeAction,
        actionArgs: { solfigrNewTreeMode },
        namespace,
      },
      { root: true },
    )
  },
  slfrm_handleNewTree({ state, commit, dispatch, getters, rootGetters }, result) {
    if (![201, 200].includes(result?.response?.status) || !result.response?.data) {
      return
    }
    const data = result.response?.data
    if (!data) return

    const custree_id = data?.id
    const treeStreet = data?.starting_street?.toLowerCase()
    let { solfigrNewTreeMode } = result

    const pathName = getRouteNameByStudyTab(router, rootGetters)

    // TODO: #REFACTOR:NAMESPACES
    // namespace is hardcoded here but should be dynamic based on split of reports and solutions
    // now its called with tmp/extra and tmp/primary depending on where the dialog is opened
    // we have to unify this and make it dynamic all the way - but its on many places and so we hardcode it here for now
    // its for separate mr indedeed (keep in mind that api_poker_custom_report_check handler is also replacing/hardcode the namespace)
    // we should also think about dynamic store module generaion with unique id as a namespace
    // now we have too many modules all with some name like tmp/extra... etc... and we have to keep track which is used where
    // and if there is some clash and so on
    const solRepNamespace = {
      StudySolutions: namespaces.STUDY,
      StudyReports: namespaces.REPORTS,
    }[pathName]

    // reset solElite before creating a new one - so the new tree values are visible in GameformatSelector
    // this is the mid-step when you have only the tree but no board yet (no solution was created)
    commit(
      'api_reset',
      { key: 'api_poker_custom_solution', namespace: solRepNamespace },
      { root: true },
    )

    if (
      pathName === 'StudySolutions' ||
      pathName === 'StudyReports' ||
      pathName === 'PracticeRangeBuilder'
    ) {
      if (solfigrNewTreeMode === 'keepboard_flop') {
        solfigrNewTreeMode = rootGetters[SNK('query_board', solRepNamespace)]
          ? 'keepboard_flop'
          : 'createnew_flop'
      }

      if (solfigrNewTreeMode === 'create_tree_then_solution_and_keepboard') {
        const street = data.starting_street.toLowerCase()
        solfigrNewTreeMode =
          street === 'flop' && rootGetters[SNK('query_board', solRepNamespace)]
            ? `keepboard_${street}`
            : `createnew_${street}`
      }

      if (
        solfigrNewTreeMode !== 'handletree_studyTab' &&
        solfigrNewTreeMode !== 'handletree_reportsTab' &&
        rootGetters[SNK('sol_usingCussolReports', solRepNamespace)]
      ) {
        solfigrNewTreeMode = 'createnew_reportsTab' // cussol was created when reports tab was active
      }

      if (treeStreet === 'preflop' && solfigrNewTreeMode === 'handletree_studyTab') {
        solfigrNewTreeMode = 'createnew_preflop'
      }

      switch (solfigrNewTreeMode) {
        case 'createnew_flop':
          dispatch('globals_action', 'disable_history_watcher', { root: true })
          setTimeout(() => {
            dispatch(
              SNK('gamestate_reset_history', solRepNamespace),
              {
                query: {
                  custree_id,
                  cussol_id: '',
                  board: '',
                },
              },
              { root: true },
            )
            dispatch(
              SNK('router_update_query', solRepNamespace),
              {
                query: {
                  custree_id,
                  cussol_id: '',
                  solution_type: 'custom',
                  dialogs: `cards-dialog${solRepNamespace ? `_namespace-${solRepNamespace}` : ''}`,
                  board: '',
                },
                push: false,
                replace: true,
                saveLocally: true,
              },
              { root: true },
            )

            setTimeout(() => {
              dispatch('globals_action', 'enable_history_watcher', { root: true })
            })
          })
          break

        case 'keepboard_flop':
        case 'keepboard_preflop':
        case 'createnew_preflop':
        case 'createnew_reportsTab':
        case 'handletree_reportsTab':
        case 'handletree_studyTab':
          dispatch('globals_action', 'disable_history_watcher', { root: true })
          setTimeout(() => {
            dispatch('router_reset_dialogs', { exeptions: [] }, { root: true })

            dispatch(
              SNK('gamestate_reset_history', solRepNamespace),
              {
                query: {
                  custree_id,
                  cussol_id: '',
                  ...(solfigrNewTreeMode === 'handletree_reportsTab'
                    ? {
                        preflop_actions: getters.router_query.preflop_actions,
                      }
                    : {}),
                },
              },
              { root: true },
            )

            dispatch(
              SNK('router_update_query', solRepNamespace),
              {
                query: {
                  custree_id,
                  cussol_id: '',
                  solution_type: 'custom',
                  ...(solfigrNewTreeMode === 'handletree_studyTab' ? { board: '' } : {}),
                },
                push: false,
                replace: true,
                saveLocally: true,
              },
              { root: true },
            )

            if (solfigrNewTreeMode.includes('reportsTab')) {
              dispatch(
                SNK(`rpfrm_getTreePreview`, solRepNamespace),
                {
                  fromStart: true,
                  customTreeId: custree_id,
                  enable_hsWatcher: true,
                },
                { root: true },
              )
              return
            }

            if (solfigrNewTreeMode !== 'handletree_studyTab') {
              dispatch(
                SNK('sol_createSolution', solRepNamespace),
                {
                  callNextActions: true,
                },
                { root: true },
              )
            }

            setTimeout(() => {
              dispatch('globals_action', 'enable_history_watcher', { root: true })
            })
          }, 0)
          break
        default:
          debugger
          break
      }
    } else if (pathName === 'PracticeTrainer' || pathName === 'PracticeDrills') {
      dispatch(
        'router_reset_dialogs',
        { exeptions: ['trainer-advanced-filter-dialog'] },
        { root: true },
      )
      let namespace = 'tra/alpha'
      let routerParams = {
        push: false,
        replace: true,
        saveLocally: true,
      }
      if (
        rootGetters.query_dialogs.split(',').some(i => i.includes('trainer-advanced-filter-dialog'))
      ) {
        namespace = 'tmp/primary'
        routerParams = {
          push: false,
          replace: false,
          saveLocally: false,
        }
      }
      dispatch(
        `${namespace}/router_update_query`,
        {
          query: {
            custree_id,
            cussol_id: '',
            solution_type: 'custom',
          },
          ...routerParams,
        },
        { root: true },
      )
    } else {
      debugger
    }
  },
  slfrm_handleSavedTreeApply({ state, commit, dispatch, getters, rootGetters }, result) {
    if (result?.response?.status !== 200 || !result.response?.data) {
      const status = result?.status
      dispatch(
        'messages',
        {
          type: 'error',
          status: 'err_message',
          statusMessages: {
            _err_message: {
              i18n: `messages.get_custom_tree_error`,
              i18nParams: { status: status || 'unknown' },
            },
          },
        },
        { root: true },
      )
      return
    }

    // create new tree from saved tree settings
    const mode = result?.mode
    const formResult = result.response?.data
    const storeAction = SNK('slfrm_handleNewTree', state.slfrm_namespace)
    const namespace = state.slfrm_namespace

    dispatch(
      'api_poker_custom_solution_tree_create',
      {
        data: formResult,
        action: storeAction,
        actionArgs: { solfigrNewTreeMode: mode },
        namespace,
      },
      { root: true },
    )
  },
  // ---
  slrfm_updateIcmInheritedValues(
    { state, commit, dispatch, rootGetters },
    { icmTournament, icmHandDetails, icmPlayersAndStacks, getTreePreview = false },
  ) {
    const conf = getSolfigrFields()

    icmTournament = icmTournament || state.slfrm_icm_tournament
    icmHandDetails = icmHandDetails || state.slfrm_icm_handDetails
    icmPlayersAndStacks = icmPlayersAndStacks || state.slfrm_icm_playersAndStacks

    const hasInstantBounty = conf.bounty_instant.is_active(
      (icmTournament || state.slfrm_icm_tournament).type,
    )
    const bountyInstantPercentage = icmTournament.bounty_instant_percentage

    let handDetails = {
      ...icmHandDetails,
      bounty_instant_IP: hasInstantBounty
        ? numFixed(icmHandDetails.bounty_total_IP * bountyInstantPercentage, 2)
        : 0,
      bounty_instant_OOP: hasInstantBounty
        ? numFixed(icmHandDetails.bounty_total_OOP * bountyInstantPercentage, 2)
        : 0,
    }

    let playersAndStacks =
      handDetails.stack_IP !== state.slfrm_icm_handDetails.stack_IP ||
      handDetails.stack_OOP !== state.slfrm_icm_handDetails.stack_OOP
        ? {
            ...icmPlayersAndStacks,
            // auto update avg_stack
            avg_stack: conf.avg_stack.getDefault({
              stacks: [
                handDetails.stack_IP,
                handDetails.stack_OOP,
                rootGetters.user_has_flag('SOL_3WAY') && handDetails.stack_OOP1,
              ].filter(Boolean),
            }),
          }
        : icmPlayersAndStacks
    playersAndStacks =
      handDetails.bounty_total_IP !== state.slfrm_icm_handDetails.bounty_total_IP ||
      handDetails.bounty_total_OOP !== state.slfrm_icm_handDetails.bounty_total_OOP
        ? {
            ...playersAndStacks,
            // auto update avg_bounty
            avg_bounty: conf.avg_bounty.getDefault(handDetails),
          }
        : playersAndStacks

    icmTournament.errors = validateTournamentStructure({
      payout_levels: icmTournament.payout_levels,
    })

    handDetails.errors = validateHandDetails({
      stack_IP: handDetails.stack_IP,
      stack_OOP: handDetails.stack_OOP,
      pot_size: handDetails.pot_size,
      stack_OOP1: handDetails.stack_OOP1,
    })

    commit('slfrm_set', {
      key: 'slfrm_icm_tournament',
      value: icmTournament,
    })
    commit('slfrm_set', {
      key: 'slfrm_icm_handDetails',
      value: handDetails,
    })
    commit('slfrm_set', {
      key: 'slfrm_icm_playersAndStacks',
      value: playersAndStacks,
    })

    if (getTreePreview) {
      dispatch('slfrm_getTreePreview', {
        fromStart: true,
      })
    }
  },
  //---------
  slfrm_updatePlayers({ commit, dispatch }, players) {
    // Update is only for flop
    const playerIP = players.find(p => p.position === 'IP')
    const playerOOP = players.find(p => p.position === 'OOP')
    const playerOOP1 = players.find(p => p.position === 'OOP+1')
    if (playerIP) {
      commit(`slfrm_set`, { key: 'slfrm_flop_playerIP', value: playerIP })
    }
    if (playerOOP) {
      commit(`slfrm_set`, { key: 'slfrm_flop_playerOOP', value: playerOOP })
    }
    if (playerOOP1) {
      commit(`slfrm_set`, {
        key: 'slfrm_flop_playerOOP1',
        value: playerOOP1,
      })
    }
    dispatch('slfrm_getTreePreview', {
      fromStart: false,
    })
  },

  //---------
  slfrm_getTreePreview(
    { state, commit, dispatch, getters, rootGetters },
    {
      hSpotsParams = null,
      fromStart = false,
      treeOperationsUpdate = false,
      filterToPrevSpot = true,
      formResult = null,
      customTreeId,
    } = {},
  ) {
    if (getters.slfrm_getFormError()) return

    const hSpot = getters.history_spot
    let hSpots = getters.history_spots

    if (filterToPrevSpot) {
      hSpots = hSpots.filter(e => e.id < hSpot.id)
    }

    if (fromStart) {
      commit('gamestate_set', {
        key: 'history_spots',
        value: [],
      })
    }

    // TODO we gotta change this to use router history params also - refresh and setup just from url dont work now
    hSpotsParams =
      hSpotsParams ||
      getHistoryUrlParams({
        history_spots: hSpots,
        includeShowdown: false,
      })

    formResult = formResult || getters.slfrm_formApiPostData

    const data = {
      preflop_actions: fromStart ? '' : hSpotsParams.preflop_actions,
      flop_actions: fromStart ? '' : hSpotsParams.flop_actions,
      turn_actions: fromStart ? '' : hSpotsParams.turn_actions,
      river_actions: fromStart ? '' : hSpotsParams.river_actions,
      ...(customTreeId
        ? {
            custom_tree_id: customTreeId,
          }
        : {
            custom_tree: {
              ...formResult,
              players: formResult.players.map(i => omit(i, 'range')),
            },
          }),
    }

    dispatch(
      'api_poker_tree_preview',
      {
        data,
        action: SNK('slfrm_handleTreePreview', state.slfrm_namespace),
        actionArgs: { fromStart, treeOperationsUpdate },
        namespace: state.slfrm_namespace,
      },
      { root: true },
    )
  },
  slfrm_handleTreePreview({ state, commit, dispatch, getters, rootGetters }, result) {
    if (result?.response?.status !== 200 || !result.response?.data) {
      const errCode = result?.error?.response?.data?.code
      if (errCode === 'NODE_DOES_NOT_EXIST') {
        // this happens when treepreview is called with non-existing actions - we reset the whole sequence to start
        dispatch('slfrm_getTreePreview', {
          customTreeId: getters.sol_elite.custom_tree_id,
          fromStart: true,
        })
      }
      return
    }
    const data = result.response?.data

    let l_hSpots = getters.history_spots
    const l_hSpot = getters.history_spot
    let noActiveSpot = false

    if (result.fromStart) {
      commit(`gamestate_set`, {
        key: 'history_spots',
        value: [],
      })
      noActiveSpot = true
    } else if (result.treeOperationsUpdate) {
      l_hSpots = l_hSpots.filter(e => e.id < l_hSpot.id)

      commit(`gamestate_set`, {
        key: 'history_spots',
        value: l_hSpots,
      })
      noActiveSpot = false
    }

    let { history_spots: hSpots } = getEventsTreePreviewApi({
      future_spots: data.future_actions,
      history_spots: data.history_actions,
    })

    const lastSpotWithSelectedAction = findLast(hSpots, i => i.selected_action_code)
    const l = hSpots.length - 1
    let hSpotId = !lastSpotWithSelectedAction
      ? 0
      : lastSpotWithSelectedAction
        ? lastSpotWithSelectedAction.id + 1
        : l

    hSpotId = hSpotId > l ? l : hSpotId
    hSpotId = noActiveSpot ? 0 : hSpotId

    hSpots = setActiveHistorySpot({
      history_spots: hSpots,
      spot_id: hSpotId,
    })

    const history_spot = hSpots[hSpotId]
    if (history_spot?.type === 'showdown') {
      hSpotId -= 1
    }

    dispatch('gamestate_set_history', {
      history_spots: hSpots,
      spot_id: hSpotId,
      push: false,
      replace: state.slfrm_namespace === 'tra/alpha', // only trainer update url
      saveLocally: true,
    })
  },

  //---------
  slfrm_handleApiRangeUpdate({ state, commit, dispatch, getters }, result) {
    dispatch('router_rm_dialog', 'solfigr-save-range-dialog', { root: true })
    dispatch('slfrm_updateTableData', { mutation: 'api_poker_saved_ranges_table', result })
    dispatch('slfrm_apiResult', {
      result,
      messagesError: {
        post: 'error_range_create',
        patch: 'error_range_update',
        delete: 'error_range_delete',
      },
      messagesSuccess: {
        post: 'success_range_create',
        patch: 'success_range_update',
        delete: 'success_range_delete',
      },
    })
  },
  slfrm_handleApiTreeUpdate({ state, commit, dispatch, getters, rootGetters }, result) {
    if (result.status === 201 || result.status === 200) {
      dispatch(
        'router_rm_dialogs',
        {
          dialogs: result.dialogs || ['solfigr-save-tree-dialog'],
        },
        { root: true },
      )
    }

    // if (result?.response?.config?.method !== 'post') {
    dispatch('slfrm_updateTableData', { mutation: 'api_poker_saved_trees_table', result })
    // }
    dispatch('slfrm_apiResult', {
      result,
      messagesError: {
        post: 'error_tree_create',
        patch: 'error_tree_update', // v1
        put: 'error_tree_update', // v4
        delete: 'error_tree_delete',
      },
      messagesSuccess: {
        post: 'success_tree_create',
        patch: 'success_tree_update', // v1
        put: 'success_tree_update', // v4
        delete: 'success_tree_delete',
      },
    })
  },
  slfrm_handleApiTreeImport({ state, commit, dispatch, getters, rootGetters }, result) {
    if (result.status === 201 || result.status === 200) {
      dispatch('router_rm_dialog', 'import-solfigr-tree-dialog')
      dispatch('slfrm_updateTableData', { mutation: 'api_poker_saved_trees_table', result })
    }
    // error handeld in component
  },
  slfrm_handleNodeUpdate({ state, commit, dispatch, getters, rootGetters }, resultVariant) {
    const result = resultVariant.response
      ? resultVariant
      : {
          method: 'POST',
          response: {
            data: resultVariant,
          },
        }

    dispatch(
      'router_rm_dialogs',
      {
        dialogs: [
          'solfigr-nodeedit-dialog', //
          'solfigr-nodelock-dialog',
        ],
      },
      { root: true },
    )

    dispatch('slfrm_apiResult', {
      result,
      messagesError: { post: 'error_nodeedit_update' },
      messagesSuccess: { post: 'success_nodeedit_update' },
    })
    const data = result.response?.data
    if (!data) return
    if (result.error) return

    commit(
      'api_update_data',
      {
        key: 'api_poker_custom_solution',
        namespace: state.slfrm_namespace,
        data,
      },
      { root: true },
    )

    const query = {
      cussol_id: data.id,
      custree_id: data.custom_tree.id,
      solution_type: 'custom',
    }

    dispatch('globals_action', 'disable_history_watcher', { root: true })
    setTimeout(() => {
      const history_spot = rootGetters[SNK('history_spot', state.slfrm_namespace)]
      const history_spots = rootGetters[SNK('history_spots', state.slfrm_namespace)]
      const hSpots = history_spots.filter(i => i.id < history_spot.id)

      dispatch(
        SNK('gamestate_set_history', state.slfrm_namespace),
        {
          namespace: '',
          history_spots: hSpots,
          query,
        },
        { root: true },
      )

      // reload whole history cos previous nodes might be changed
      dispatch(
        'api_poker_history',
        {
          namespace: state.slfrm_namespace,
          enable_hsWatcher: true,
          cussol_id: query.cussol_id,
          routerParams: {
            push: true,
            replace: false,
            saveLocally: true,
          },
        },
        { root: true },
      )
    })
  },
  slfrm_updateTableData(
    { state, commit, dispatch, getters, rootGetters },
    { mutation, result, idKey = 'id' },
  ) {
    const status = result?.response?.status
    if (!(status === 201 || status === 200)) return

    const apiData = rootGetters.api_get_data(mutation)
    if (!apiData) return

    const item = result.response?.data

    let results = []
    const tableData = apiData.results || apiData || []
    const currItem = tableData.find(i => i[idKey] === item[idKey])
    if (currItem) {
      results = tableData.map(i => {
        if (i[idKey] === item[idKey]) {
          return { ...item }
        }
        return i
      })
    } else {
      results = [item, ...(apiData.results || apiData || [])]
    }

    commit(
      'api_update_data',
      {
        key: mutation,
        data: apiData.results ? { ...apiData, results } : results,
      },
      { root: true },
    )
  },
  slfrm_apiResult(
    { state, commit, dispatch, getters },
    { result, messagesError, messagesSuccess },
  ) {
    const status = result?.status
    const method = result?.method
    if (result?.error) {
      const message = messagesError[method.toLowerCase()]
      dispatch(
        'messages',
        {
          type: 'error',
          status: 'err_message',
          statusMessages: {
            _err_message: {
              i18n: `messages.${message}`,
              i18nParams: { status: status || 'unknown' },
            },
          },
        },
        { root: true },
      )
    } else {
      const message = messagesSuccess[method.toLowerCase()]
      dispatch(
        'messages',
        {
          type: 'success',
          status: 'success_message',
          statusMessages: {
            _success_message: {
              i18n: `messages.${message}`,
            },
          },
          hide: true,
        },
        { root: true },
      )
    }
  },
}

const mutations = {
  slfrm_reset(state, { slfrm_startStreet = 'preflop' } = {}) {
    state = {
      ...state,
      ...getInitialState({ namespace: state.slfrm_namespace, slfrm_startStreet }),
    }
  },
  slfrm_set(state, { key, value }) {
    state[key] = value
  },
}

function getSolfigrModule(namespace) {
  return {
    state: getInitialState({ namespace }),
    getters,
    actions,
    mutations,
  }
}

export { getSolfigrModule }
